/**
 * OosDepthPanel (C-6) — FDA Phase-1 laboratory-investigation checklist +
 * retest / resample tracking, for the OOS investigation screen.
 */
import { useState } from 'react';
import { apiFetch } from '../../api/client';
import styles from './OosDepthPanel.module.css';
import type { Phase1ChecklistItem, OosRetest } from './useOosDetailQuery';

// Standard FDA Phase-1 laboratory-error hypotheses (rule each out before Phase 2).
const STANDARD_HYPOTHESES = [
  'Analyst interview — result confirmed',
  'Calculation / data-entry error',
  'Instrument malfunction / calibration status',
  'Sample preparation error (weighing / dilution)',
  'Standard / reagent / mobile-phase integrity',
  'Glassware / contamination',
  'Method followed correctly (system suitability)',
  'Column / consumable performance',
];
function fmtDT(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso); }
}

export function OosDepthPanel({
  oosId, phase1Checklist, retests, onChanged,
}: {
  oosId: string;
  phase1Checklist: Phase1ChecklistItem[];
  retests: OosRetest[];
  onChanged: () => void;
}) {
  // Seed the editable checklist from saved state, merged with the standard list.
  const saved = new Map(phase1Checklist.map((c) => [c.hypothesis, c]));
  const [items, setItems] = useState<Phase1ChecklistItem[]>(
    STANDARD_HYPOTHESES.map((h) => saved.get(h) ?? { hypothesis: h, ruledOut: false, note: null })
  );
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const [rtKind, setRtKind] = useState<'RETEST' | 'RESAMPLE'>('RETEST');
  const [rtVal, setRtVal] = useState('');
  const [rtUnits, setRtUnits] = useState('');
  const [rtOutcome, setRtOutcome] = useState<'WITHIN_SPEC' | 'OUT_OF_SPEC'>('WITHIN_SPEC');
  const [rtNotes, setRtNotes] = useState('');

  function toggle(i: number, ruledOut: boolean) {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ruledOut } : it)));
  }
  function setNote(i: number, note: string) {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, note } : it)));
  }
  async function saveChecklist() {
    setMsg(null); setBusy(true);
    try { await apiFetch(`/api/v1/oos/${encodeURIComponent(oosId)}/phase1-checklist`, { method: 'POST', body: { checklist: items } }); onChanged(); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Could not save the checklist.'); }
    finally { setBusy(false); }
  }
  async function addRetest() {
    setMsg(null); setBusy(true);
    try {
      await apiFetch(`/api/v1/oos/${encodeURIComponent(oosId)}/retest`, {
        method: 'POST',
        body: { kind: rtKind, resultValue: rtVal || null, resultUnits: rtUnits || null, outcome: rtOutcome, notes: rtNotes || null },
      });
      setRtVal(''); setRtUnits(''); setRtNotes(''); onChanged();
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Could not record the retest.'); }
    finally { setBusy(false); }
  }

  const ruledOutCount = items.filter((i) => i.ruledOut).length;

  return (
    <>
      <section className={styles.panel} aria-labelledby="oos-p1">
        <header className={styles.head}>
          <h2 id="oos-p1" className={styles.title}>Phase-1 lab investigation</h2>
          <span className={styles.count}>{ruledOutCount}/{items.length} ruled out</span>
          <button type="button" className={styles.btnPrimary} onClick={() => void saveChecklist()} disabled={busy}>Save checklist</button>
        </header>
        {msg && <div className={styles.err} role="alert">{msg}</div>}
        <ul className={styles.checklist}>
          {items.map((it, i) => (
            <li key={it.hypothesis} className={styles.checkRow}>
              <label className={styles.checkLabel}>
                <input type="checkbox" checked={it.ruledOut} onChange={(e) => toggle(i, e.target.checked)} />
                <span>{it.hypothesis}</span>
              </label>
              <input type="text" className={styles.noteInput} placeholder="Finding / note" value={it.note ?? ''} onChange={(e) => setNote(i, e.target.value)} />
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.panel} aria-labelledby="oos-rt">
        <header className={styles.head}>
          <h2 id="oos-rt" className={styles.title}>Retests &amp; resamples</h2>
          <span className={styles.count}>{retests.length}</span>
        </header>
        {retests.length === 0 ? (
          <div className={styles.empty}>No retests or resamples recorded.</div>
        ) : (
          <ul className={styles.rtList}>
            {retests.map((r) => (
              <li key={r.retestId} className={styles.rtRow}>
                <span className={styles.rtKind}>{r.kind}</span>
                <span className={`${styles.rtOutcome} ${r.outcome === 'WITHIN_SPEC' ? styles.ocGood : styles.ocBad}`}>{r.outcome === 'WITHIN_SPEC' ? 'Within spec' : 'Out of spec'}</span>
                <span className={styles.rtMeta}>
                  {r.resultValue != null ? `${r.resultValue}${r.resultUnits ? ' ' + r.resultUnits : ''}` : '—'}
                  {r.notes ? ` · ${r.notes}` : ''}
                  <span className={styles.sub}> · {fmtDT(r.performedAtUtc)}{r.performedByName ? ` · ${r.performedByName}` : ''}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className={styles.rtForm}>
          <select value={rtKind} onChange={(e) => setRtKind(e.target.value as 'RETEST' | 'RESAMPLE')} className={styles.select} aria-label="Retest kind">
            <option value="RETEST">Retest</option>
            <option value="RESAMPLE">Resample</option>
          </select>
          <input type="number" value={rtVal} onChange={(e) => setRtVal(e.target.value)} placeholder="Result" className={styles.numInput} />
          <input type="text" value={rtUnits} onChange={(e) => setRtUnits(e.target.value)} placeholder="Units" className={styles.unitInput} />
          <select value={rtOutcome} onChange={(e) => setRtOutcome(e.target.value as 'WITHIN_SPEC' | 'OUT_OF_SPEC')} className={styles.select} aria-label="Retest outcome">
            <option value="WITHIN_SPEC">Within spec</option>
            <option value="OUT_OF_SPEC">Out of spec</option>
          </select>
          <input type="text" value={rtNotes} onChange={(e) => setRtNotes(e.target.value)} placeholder="Notes" className={styles.notesInput} />
          <button type="button" className={styles.btnPrimary} onClick={() => void addRetest()} disabled={busy}>Add</button>
        </div>
      </section>
    </>
  );
}
